import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { loadInstallmentsByCompany, getInstallmentStatus } from '../../../core/utils/installment';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import {
  ENTRY_TYPE,
  INSTALLMENT_STATUS,
  INSTALLMENT_STATUS_COLORS,
  INSTALLMENT_STATUS_FILTERS,
} from '../../../core/constants/financial';
import { getRangeFromFilter, getPeriodLabel, todayISO } from '../../../core/utils/financialPeriod';
import { useLogoPreloader } from '../../../core/hooks/useLogoPreloader';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import FinancialListModal from './FinancialListModal';
import FinancialInstallmentsPrint from './FinancialInstallmentsPrint';
import PeriodFilter from './PeriodFilter';
import StatusFilter from './StatusFilter';
import PaymentModal from './PaymentModal';
import FinancialEntryModal from './FinancialEntryModal';

const PRINT_PAGE_STYLE = `
  @page { size: A4 portrait; margin: 10mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const getStatusFilterLabel = (key) => {
  const option = INSTALLMENT_STATUS_FILTERS.find((s) => s.key === key);
  return option ? option.label : '';
};

// Referência estável — inline recriaria o objeto a cada render e o efeito de
// prefill do FinancialEntryModal apagaria o form digitado.
const NEW_ENTRY_PREFILL = { type: ENTRY_TYPE.DESPESA };

const SORT_ACCESSORS = {
  fornecedor: (i) => i.financial_entry?.supplier_client || '',
  plano: (i) => i.financial_entry?.AccountPlan?.name || '',
  vencimento: (i) => i.due_date || '',
  valor: (i) => Number(i.value) || 0,
  parcela: (i) => i.installment_number || '',
  situacao: (i) => getInstallmentStatus(i),
};

// Situação exibida por parcela: pago > atrasado (pendente vencido) > pendente.
const getStatusDisplay = (inst) => {
  if (inst.status === INSTALLMENT_STATUS.PAGO) {
    return { text: 'PAGO', color: '#70E000', bg: 'rgba(112,224,0,0.15)' };
  }
  if (inst.due_date && inst.due_date < todayISO()) {
    return { text: 'ATRASADO', color: '#ff5a5a', bg: 'rgba(255,90,90,0.15)' };
  }
  return { text: 'PENDENTE', color: '#e88c2a', bg: 'rgba(232,140,42,0.15)' };
};

const AccountsPayableModal = ({ isOpen, onClose, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});
  const [installments, setInstallments] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todos');
  const [customRange, setCustomRange] = useState({ from: todayISO(), to: todayISO() });
  const [statusFilter, setStatusFilter] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setFilter('todos');
    setCustomRange({ from: todayISO(), to: todayISO() });
    setStatusFilter([]);
  }, [isOpen]);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [editEntryId, setEditEntryId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const printRef = useRef();
  const { logoBase64, waitForReady } = useLogoPreloader(company?.logo);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: PRINT_PAGE_STYLE,
    onBeforeGetContent: () => waitForReady(4000),
  });

  const range = getRangeFromFilter(filter, customRange);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    loadInstallmentsByCompany(company.id, {
      type: ENTRY_TYPE.DESPESA,
      dueDateFrom: range.from,
      dueDateTo: range.to,
    }).then((data) => {
      if (cancelled) return;
      setInstallments(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, filter, customRange.from, customRange.to, refreshKey]);

  const filtered = installments.filter((i) => {
    if (statusFilter.length > 0 && !statusFilter.includes(getInstallmentStatus(i))) return false;
    const term = search.toLowerCase();
    if (!term) return true;
    const supplier = i.financial_entry?.supplier_client?.toLowerCase() || '';
    const account = i.financial_entry?.AccountPlan?.name?.toLowerCase() || '';
    return supplier.includes(term) || account.includes(term);
  });

  const totalPendente = filtered
    .filter((i) => i.status !== INSTALLMENT_STATUS.PAGO)
    .reduce((sum, i) => sum + (Number(i.value) || 0), 0);

  const totalCount = filtered.length;
  const totalExibido = filtered.reduce((sum, i) => sum + (Number(i.value) || 0), 0);

  const { sorted, sort, toggleSort } = useTableSort(filtered, SORT_ACCESSORS);

  return (
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title="Contas a Pagar"
      badge={`${formatCurrency(totalExibido)} no período`}
      searchPlaceholder="Buscar fornecedor ou conta..."
      search={search}
      onSearchChange={setSearch}
      onNew={() => setCreateOpen(true)}
      newLabel="+ Adicionar Lançamento"
      headerExtra={
        <button type="button" className="fd-btn-green" onClick={handlePrint}>
          Imprimir
        </button>
      }
    >
      <PeriodFilter
        filter={filter}
        onFilterChange={setFilter}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      <StatusFilter value={statusFilter} onChange={setStatusFilter} />

      <div className="fd-modal-table-wrapper">
        <table className="fd-modal-table">
          <thead>
            <tr>
              <SortableTh label="Fornecedor" sortKey="fornecedor" sort={sort} onSort={toggleSort} />
              <SortableTh label="Plano de Contas" sortKey="plano" sort={sort} onSort={toggleSort} />
              <SortableTh label="Vencimento" sortKey="vencimento" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
              <SortableTh label="Valor" sortKey="valor" sort={sort} onSort={toggleSort} style={{ width: 130, textAlign: 'right' }} />
              <SortableTh label="Parcela" sortKey="parcela" sort={sort} onSort={toggleSort} style={{ width: 80, textAlign: 'center' }} />
              <SortableTh label="Situação" sortKey="situacao" sort={sort} onSort={toggleSort} style={{ width: 100, textAlign: 'center' }} />
              <th style={{ width: 100, textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((inst) => {
              const st = getInstallmentStatus(inst);
              const c = INSTALLMENT_STATUS_COLORS[st];
              return (
                <tr
                  key={inst.id}
                  onClick={() =>
                    setEditEntryId(inst.financial_entry_id || inst.financial_entry?.id)
                  }
                  style={{ cursor: 'pointer' }}
                  title="Clique para abrir o lançamento"
                >
                  <td>{inst.financial_entry?.supplier_client || '—'}</td>
                  <td className="fd-table-muted">{inst.financial_entry?.AccountPlan?.name || '—'}</td>
                  <td style={{ color: st === INSTALLMENT_STATUS.ATRASADO ? '#f16063' : '#e88c2a' }}>
                    {formatDateBR(inst.due_date)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(inst.value)}</td>
                  <td className="fd-table-muted" style={{ textAlign: 'center' }}>{inst.installment_number}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="fd-status-badge" style={{ background: c.bg, color: c.color }}>
                      {c.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {st === INSTALLMENT_STATUS.PAGO ? (
                      <span className="fd-table-muted">—</span>
                    ) : (
                      <button
                        type="button"
                        className="fd-btn-green"
                        style={{ padding: '5px 12px', fontSize: 11 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPaymentTarget(inst);
                        }}
                      >
                        PAGAR
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="fd-table-empty">Nenhuma conta a pagar no período</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1000px' }}>
        <FinancialInstallmentsPrint
          ref={printRef}
          company={company}
          logoBase64={logoBase64}
          reportTitle="Contas a Pagar"
          entityLabel="Fornecedor"
          periodLabel={getPeriodLabel(filter, customRange)}
          statusLabel={statusFilter.map(getStatusFilterLabel).join(', ')}
          searchTerm={search}
          data={sorted}
        />
      </div>

      <PaymentModal
        isOpen={!!paymentTarget}
        installment={paymentTarget}
        mode="pagar"
        onClose={() => setPaymentTarget(null)}
        onConfirmed={() => {
          setRefreshKey((k) => k + 1);
          if (onChanged) onChanged();
        }}
      />

      <FinancialEntryModal
        isOpen={createOpen || editEntryId != null}
        entryId={editEntryId}
        prefilled={NEW_ENTRY_PREFILL}
        onClose={() => {
          setCreateOpen(false);
          setEditEntryId(null);
        }}
        onSaved={() => {
          setRefreshKey((k) => k + 1);
          if (onChanged) onChanged();
        }}
      />
    </FinancialListModal>
  );
};

export default AccountsPayableModal;
